import type { Plugin } from 'vite'

// Public destination supplied for this site. Conversion labels are separate.
export const GOOGLE_ADS_DESTINATION_ID = 'AW-16565114295'

export interface AnalyticsConfig {
  enabled: boolean
  posthogToken: string
  posthogHost: string
  ga4Id: string
  googleAdsId: string
  stagingPosthog?: boolean
}

export function readAnalyticsConfig(env: Record<string, string | undefined>): AnalyticsConfig {
  if (env.VITE_BORG_ANALYTICS_ENABLED && !['true', 'false'].includes(env.VITE_BORG_ANALYTICS_ENABLED)) {
    throw new Error('VITE_BORG_ANALYTICS_ENABLED must be true or false')
  }
  const enabled = env.VITE_BORG_ANALYTICS_ENABLED === 'true'
  const posthogToken = env.VITE_POSTHOG_PROJECT_TOKEN?.trim() ?? ''
  let posthogHost = env.VITE_POSTHOG_HOST?.trim().replace(/\/$/, '') ?? ''
  const ga4Id = env.VITE_GA4_MEASUREMENT_ID?.trim() ?? ''
  const googleAdsId = env.VITE_GOOGLE_ADS_DESTINATION_ID?.trim() ?? ''
  const stagingPosthog = env.VITE_BORG_STAGING_POSTHOG === 'true'
  if (stagingPosthog && (!enabled || !/^phc_[A-Za-z0-9_]+$/.test(posthogToken) || !posthogHost || ga4Id)) {
    throw new Error('Staging PostHog requires an enabled dedicated project and excludes GA4')
  }
  if (googleAdsId && googleAdsId !== GOOGLE_ADS_DESTINATION_ID) {
    throw new Error(`Google Ads requires the reviewed destination ${GOOGLE_ADS_DESTINATION_ID}`)
  }
  if (enabled) {
    if (!posthogToken && !ga4Id && !googleAdsId) throw new Error('Enabled analytics requires PostHog, GA4 or Google Ads configuration')
    if (Boolean(posthogToken) !== Boolean(posthogHost)) throw new Error('PostHog requires both a project token and host')
    if (posthogHost) {
      const host = new URL(posthogHost)
      if (host.protocol !== 'https:' || host.username || host.password || host.search || host.hash) {
        throw new Error('PostHog host must be an HTTPS URL without credentials, query or fragment')
      }
      // Twingate resolves this legacy origin into Chrome's local address space.
      // The existing public proxy forwards ingestion to the same PostHog project.
      if (host.origin === 'https://posthog.internal.searchatlas.com' && host.pathname === '/') {
        posthogHost = 'https://searchatlas.com/ingest'
      }
    }
    if (ga4Id && !/^G-[A-Z0-9]+$/.test(ga4Id)) throw new Error('GA4 requires a G- measurement ID')
  }
  return { enabled, posthogToken, posthogHost, ga4Id, googleAdsId, ...(stagingPosthog ? { stagingPosthog: true } : {}) }
}

// Every Vite HTML entry gets the same bundled module, including landing pages.
export function analyticsEntry(): Plugin {
  return {
    name: 'borg-site-analytics',
    transformIndexHtml: {
      order: 'pre',
      handler: () => [{ tag: 'script', attrs: { type: 'module', src: '/src/analytics.ts' }, injectTo: 'head' }],
    },
  }
}
